import { useEffect, useState } from "react";
import { Link, useNavigate, NavLink, Routes, Route, Navigate } from "react-router-dom";
import {
  LayoutDashboard, Inbox, Star, Settings, BarChart3, Building2,
  Phone, Mail, Calendar, ExternalLink, CheckCircle2, Clock, Loader2,
  ImagePlus, Save, MapPin, Plus, Trash2, Edit3, MessageCircleQuestion, ChevronDown, MessageSquare, EyeOff, Eye,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import StarRating from "@/components/StarRating";
import PhotoUploader from "@/components/PhotoUploader";
import { DAY_LABELS } from "@/lib/constants";
import RichTextEditor from "@/components/RichTextEditor";
import { Send } from "lucide-react";

const NAV = [
  { to: "overview", icon: LayoutDashboard, label: "Overview" },
  { to: "locations", icon: MapPin, label: "My Locations" },
  { to: "analytics", icon: BarChart3, label: "Analytics" },
  { to: "leads", icon: Inbox, label: "Lead Inbox" },
  { to: "reviews", icon: Star, label: "Reviews" },
  { to: "faqs", icon: MessageCircleQuestion, label: "FAQs" },
  { to: "qa", icon: MessageSquare, label: "Customer Q&A" },
  { to: "edit", icon: Settings, label: "Edit Listing" },
];

export default function BusinessDashboard() {
  const { user } = useAuth();
  const [providers, setProviders] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/me/providers").then(({ data }) => {
      setProviders(data);
      if (data.length) setActive(data[0]);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#064E3B]" /></div>;
  }

  if (providers.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <Building2 className="w-12 h-12 text-stone-300 mx-auto mb-4" />
        <h1 className="font-display text-2xl font-semibold mb-2">No listings yet</h1>
        <p className="text-stone-600 mb-6">You haven't added or claimed a business listing.</p>
        <div className="flex gap-3 justify-center">
          <Button asChild className="bg-[#064E3B] hover:bg-[#053d2f]"><Link to="/add-business" data-testid="dash-add-business">List your business</Link></Button>
          <Button asChild variant="outline"><Link to="/claim-business">Claim existing</Link></Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#10B981] mb-1">Business Dashboard</div>
          <h1 className="font-display text-3xl font-semibold">Welcome back, {user.name?.split(" ")[0]}</h1>
        </div>
        {providers.length > 1 && (
          <Select value={active?.id} onValueChange={(id) => setActive(providers.find((p) => p.id === id))}>
            <SelectTrigger className="w-[280px]" data-testid="business-switcher"><SelectValue /></SelectTrigger>
            <SelectContent>
              {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {active && (
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
          <aside className="space-y-1">
            <div className="mb-4 p-3 rounded-xl bg-stone-50 border border-stone-200">
              <div className="text-xs text-stone-500 mb-1">Listing status</div>
              <Badge className={
                active.status === "active" ? "bg-[#10B981] hover:bg-[#10B981]" :
                active.status === "pending" ? "bg-amber-500 hover:bg-amber-500" :
                "bg-stone-400"
              }>{active.status}</Badge>
              <div className="mt-2 text-sm font-display font-semibold text-stone-900 truncate">{active.name}</div>
              <Link to={`/listing/${active.slug}`} target="_blank" className="text-xs text-[#064E3B] hover:underline inline-flex items-center gap-1 mt-1">
                View public page <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={`/dashboard/business/${n.to}`}
                end={n.to === "overview"}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition ${isActive ? "bg-[#064E3B] text-white" : "text-stone-700 hover:bg-stone-100"}`
                }
                data-testid={`dash-nav-${n.to}`}
              >
                <n.icon className="w-4 h-4" />
                {n.label}
              </NavLink>
            ))}
          </aside>

          <main>
            <Routes>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<Overview provider={active} />} />
              <Route path="locations" element={
                <MyLocations
                  providers={providers}
                  active={active}
                  onChange={(updated) => {
                    setProviders(updated);
                    if (active && !updated.find((p) => p.id === active.id)) {
                      setActive(updated[0] || null);
                    }
                  }}
                  onSwitch={(p) => setActive(p)}
                />
              } />
              <Route path="leads" element={<Leads />} />
              <Route path="analytics" element={
                <Analytics
                  provider={active}
                  providers={providers}
                  onSwitch={(p) => setActive(p)}
                />
              } />
              <Route path="reviews" element={<Reviews provider={active} />} />
              <Route path="faqs" element={<FAQManager provider={active} providers={providers} onSwitch={(p) => setActive(p)} />} />
              <Route path="qa" element={<QAManager provider={active} providers={providers} onSwitch={(p) => setActive(p)} />} />
              <Route path="edit" element={<EditListing provider={active} onUpdate={(p) => {
                setActive(p);
                setProviders(providers.map((x) => x.id === p.id ? p : x));
              }} />} />
            </Routes>
          </main>
        </div>
      )}
    </div>
  );
}

function Overview({ provider }) {
  const [leadCount, setLeadCount] = useState(0);
  const [newLeadCount, setNewLeadCount] = useState(0);

  useEffect(() => {
    api.get("/owner/leads").then(({ data }) => {
      setLeadCount(data.length);
      setNewLeadCount(data.filter((l) => l.status === "new").length);
    });
  }, []);

  const completeness = (() => {
    let score = 0, max = 8;
    if (provider.description?.length > 100) score++;
    if (provider.services?.length > 0) score++;
    if (provider.gallery?.length >= 3) score++;
    if (provider.hours && Object.keys(provider.hours).length) score++;
    if (provider.medical_director) score++;
    if (provider.specialties?.length) score++;
    if (provider.tagline) score++;
    if (provider.why_choose_us?.length) score++;
    return Math.round((score / max) * 100);
  })();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Listing status" value={provider.status} accent={provider.status === "active" ? "green" : "amber"} />
        <StatCard label="Reviews" value={provider.review_count} sub={`★ ${provider.avg_rating?.toFixed(1) || "—"}`} />
        <StatCard label="Total leads" value={leadCount} />
        <StatCard label="New leads" value={newLeadCount} accent="green" />
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 p-6">
        <div className="flex justify-between items-end mb-2">
          <h3 className="font-display text-lg font-semibold">Profile completeness</h3>
          <span className="font-display text-2xl font-semibold text-[#064E3B]">{completeness}%</span>
        </div>
        <div className="h-2 rounded-full bg-stone-100 overflow-hidden mb-4">
          <div className="h-full bg-[#10B981] transition-all" style={{ width: `${completeness}%` }} />
        </div>
        <ul className="space-y-1.5 text-sm">
          {[
            ["description", "Detailed description (100+ chars)", provider.description?.length > 100],
            ["services", "At least one service added", provider.services?.length > 0],
            ["gallery", "3+ gallery photos", provider.gallery?.length >= 3],
            ["specialties", "Specialties tagged", provider.specialties?.length],
            ["tagline", "Tagline added", provider.tagline],
            ["why_choose_us", "Why choose us section", provider.why_choose_us?.length],
          ].map(([k, l, done]) => (
            <li key={k} className={`flex items-center gap-2 ${done ? "text-stone-400 line-through" : "text-stone-700"}`}>
              {done ? <CheckCircle2 className="w-4 h-4 text-[#10B981]" /> : <Clock className="w-4 h-4 text-amber-500" />} {l}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, accent }) {
  const colors = { green: "text-[#10B981]", amber: "text-amber-600" };
  return (
    <div className="bg-white rounded-xl border border-stone-200 p-4">
      <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-1">{label}</div>
      <div className={`font-display text-2xl font-semibold ${colors[accent] || "text-stone-900"}`}>{value}</div>
      {sub && <div className="text-xs text-stone-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/owner/leads").then(({ data }) => setLeads(data)).finally(() => setLoading(false));
  }, []);

  const updateStatus = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    setLeads(leads.map((l) => l.id === id ? { ...l, status } : l));
    toast.success("Status updated");
  };

  const filtered = filter === "all" ? leads : leads.filter((l) => l.status === filter);

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[#064E3B]" />;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-xl font-semibold">Lead Inbox</h2>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All leads</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="booked">Booked</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
          <Inbox className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <p className="text-sm text-stone-500">No leads yet — they'll appear here as patients submit requests.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((l) => (
            <div key={l.id} className="bg-white rounded-xl border border-stone-200 p-5" data-testid={`lead-card-${l.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-display font-semibold text-stone-900">{l.name}</h4>
                    <Badge variant="outline" className={
                      l.status === "new" ? "border-[#10B981] text-[#10B981]" :
                      l.status === "booked" ? "border-[#064E3B] text-[#064E3B]" :
                      "border-stone-300 text-stone-500"
                    }>{l.status}</Badge>
                    {l.kind === "inquiry" && (
                      <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 border-0 text-[10px]">Quick inquiry</Badge>
                    )}
                  </div>
                  <div className="text-xs text-stone-500 mb-2">{new Date(l.created_at).toLocaleString()}</div>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-stone-700">
                    <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-stone-400" /><a href={`tel:${l.phone}`} className="hover:text-[#064E3B]">{l.phone}</a></div>
                    {l.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-stone-400" /><a href={`mailto:${l.email}`} className="hover:text-[#064E3B]">{l.email}</a></div>}
                    {l.service && <div>Service: <strong>{l.service}</strong></div>}
                    {l.preferred_date && <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-stone-400" />Preferred: {l.preferred_date}</div>}
                    {l.time_window && <div>Window: <strong>{l.time_window}</strong></div>}
                  </div>
                  {l.health_conditions && (
                    <div className="text-sm text-stone-700 mt-2"><span className="text-stone-500 text-xs uppercase font-semibold mr-2">Health notes:</span>{l.health_conditions}</div>
                  )}
                  {l.service_questions && (
                    <div className="text-sm text-stone-700 mt-1"><span className="text-stone-500 text-xs uppercase font-semibold mr-2">Questions:</span>{l.service_questions}</div>
                  )}
                  {l.message && <p className="text-sm text-stone-600 mt-2 italic">"{l.message}"</p>}
                </div>
                <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                  <SelectTrigger className="w-[140px]" data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="booked">Booked</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Reviews({ provider }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [replyText, setReplyText] = useState("");

  useEffect(() => {
    api.get(`/providers/${provider.id}/reviews`).then(({ data }) => setReviews(data)).finally(() => setLoading(false));
  }, [provider.id]);

  const saveReply = async (reviewId) => {
    await api.post(`/reviews/${reviewId}/reply`, { reply: replyText });
    setReviews(reviews.map((r) => r.id === reviewId ? { ...r, owner_reply: replyText } : r));
    setEditing(null);
    setReplyText("");
    toast.success("Reply posted");
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[#064E3B]" />;

  return (
    <div className="space-y-6">
      <RequestReviewsPanel provider={provider} />

      <div>
        <h2 className="font-display text-xl font-semibold mb-5">Reviews</h2>
        {reviews.length === 0 ? (
          <p className="text-stone-500 text-sm">No reviews yet.</p>
        ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div key={r.id} className="bg-white rounded-xl border border-stone-200 p-5">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-display font-semibold text-stone-900 text-sm">{r.user_name}</div>
                  <StarRating value={r.rating} size={13} />
                </div>
                <div className="text-xs text-stone-500">{new Date(r.created_at).toLocaleDateString()}</div>
              </div>
              <h4 className="font-display font-semibold mb-1">{r.title}</h4>
              <p className="text-sm text-stone-700 mb-3">{r.body}</p>
              {r.owner_reply ? (
                <div className="pl-3 border-l-2 border-[#064E3B]/30 bg-[#F3F4F1] rounded-r-lg p-3 text-sm">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#064E3B] mb-1">Your response</div>
                  {r.owner_reply}
                  <Button size="sm" variant="ghost" className="ml-2 h-auto py-0 text-xs" onClick={() => { setEditing(r.id); setReplyText(r.owner_reply); }}>Edit</Button>
                </div>
              ) : editing !== r.id ? (
                <Button size="sm" variant="outline" onClick={() => { setEditing(r.id); setReplyText(""); }} data-testid={`reply-btn-${r.id}`}>
                  Reply publicly
                </Button>
              ) : null}
              {editing === r.id && (
                <div className="mt-2">
                  <Textarea rows={2} value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Thank you for the kind words..." data-testid={`reply-textarea-${r.id}`} />
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" className="bg-[#064E3B] hover:bg-[#053d2f]" onClick={() => saveReply(r.id)} data-testid={`reply-save-${r.id}`}>Post reply</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}

function RequestReviewsPanel({ provider }) {
  const [emails, setEmails] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get(`/owner/review-requests?provider_id=${provider.id}`).then(({ data }) => setHistory(data)).catch(() => {});
  }, [provider.id]);

  const send = async (e) => {
    e.preventDefault();
    const list = emails.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) {
      toast.error("Add at least one email address");
      return;
    }
    if (list.length > 50) {
      toast.error("Maximum 50 emails per batch");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/owner/review-requests", {
        provider_id: provider.id,
        emails: list,
        custom_message: customMessage,
      });
      toast.success(`${data.sent} review request${data.sent === 1 ? "" : "s"} sent`);
      setEmails("");
      setCustomMessage("");
      const fresh = await api.get(`/owner/review-requests?provider_id=${provider.id}`);
      setHistory(fresh.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#064E3B] to-[#0a6b54] text-white rounded-2xl p-6" data-testid="request-reviews-panel">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
          <Mail className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-white/70 font-semibold mb-1">Grow your social proof</div>
          <h3 className="font-display text-xl font-semibold">Request reviews from past patients</h3>
          <p className="text-sm text-white/80 mt-1">Send personalized review requests in seconds. Each email links directly to write a review for {provider.name}.</p>
        </div>
      </div>

      <form onSubmit={send} className="space-y-3">
        <div>
          <Label className="text-xs uppercase font-semibold tracking-wider text-white/80">Patient emails (one per line, or comma-separated)</Label>
          <Textarea
            rows={3}
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            placeholder="jane@example.com&#10;john@example.com"
            className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-white/30 mt-1"
            data-testid="review-request-emails"
          />
        </div>
        <div>
          <Label className="text-xs uppercase font-semibold tracking-wider text-white/80">Personal message (optional)</Label>
          <Input
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="Hope you had a great visit!"
            className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-white/30 mt-1"
          />
        </div>
        <Button type="submit" disabled={sending} className="bg-white text-[#064E3B] hover:bg-stone-100 rounded-full font-semibold" data-testid="review-request-send">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Send review requests</>}
        </Button>
      </form>

      {history.length > 0 && (
        <div className="mt-5 pt-5 border-t border-white/15">
          <div className="text-xs uppercase tracking-wider text-white/70 font-semibold mb-2">Recent requests · {history.length} sent</div>
          <div className="space-y-1.5 max-h-32 overflow-y-auto">
            {history.slice(0, 10).map((r) => (
              <div key={r.id} className="flex justify-between text-xs text-white/90 bg-white/5 rounded-lg px-2.5 py-1.5">
                <span>{r.patient_email}</span>
                <span className="text-white/60">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EditListing({ provider, onUpdate }) {
  const [form, setForm] = useState({
    name: "", tagline: "", description: "",
    phone: "", email: "", website: "",
    address: "", city: "", state: "",
    medical_director: "",
    hero_image: "", gallery: [],
    accepts_walkins: false, accepts_insurance: false,
    home_visits: false, licensed_nurses: false,
  });
  const [saving, setSaving] = useState(false);

  // Re-seed form when the active provider changes (or first arrives)
  useEffect(() => {
    if (!provider) return;
    setForm({
      name: provider.name || "",
      tagline: provider.tagline || "",
      description: provider.description || "",
      phone: provider.phone || "",
      email: provider.email || "",
      website: provider.website || "",
      address: provider.address || "",
      city: provider.city || "",
      state: provider.state || "",
      medical_director: provider.medical_director || "",
      hero_image: provider.hero_image || "",
      gallery: provider.gallery || [],
      accepts_walkins: !!provider.accepts_walkins,
      accepts_insurance: !!provider.accepts_insurance,
      home_visits: !!provider.home_visits,
      licensed_nurses: !!provider.licensed_nurses,
    });
  }, [provider?.id]);

  if (!provider) {
    return (
      <div className="bg-white rounded-2xl border border-stone-200 p-8 text-center">
        <p className="text-stone-500 text-sm">Select a location to edit from the dropdown above, or add a new one from <strong>My Locations</strong>.</p>
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, hero_image: form.gallery?.[0] || form.hero_image };
      const { data } = await api.patch(`/providers/${provider.id}`, payload);
      onUpdate(data);
      toast.success("Listing updated");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="font-display text-xl font-semibold">Edit listing</h2>

      <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4">
        <h3 className="font-display font-semibold">Photos</h3>
        <PhotoUploader
          photos={form.gallery}
          onChange={(g) => setForm({ ...form, gallery: g, hero_image: g[0] || "" })}
          max={10}
        />
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 p-6 grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label className="text-xs uppercase font-semibold tracking-wider">Business name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs uppercase font-semibold tracking-wider">Tagline</Label>
          <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs uppercase font-semibold tracking-wider">Description</Label>
          <Textarea rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="edit-description" />
        </div>
        <div>
          <Label className="text-xs uppercase font-semibold tracking-wider">Phone</Label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs uppercase font-semibold tracking-wider">Website</Label>
          <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs uppercase font-semibold tracking-wider">City</Label>
          <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs uppercase font-semibold tracking-wider">State</Label>
          <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs uppercase font-semibold tracking-wider">Medical director</Label>
          <Input value={form.medical_director} onChange={(e) => setForm({ ...form, medical_director: e.target.value })} />
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#064E3B] hover:bg-[#053d2f]" data-testid="edit-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save changes</>}
        </Button>
      </div>
    </div>
  );
}

// ============== MY LOCATIONS ==============

const initialHours = Object.fromEntries(
  DAY_LABELS.map((d) => [d.key, { open: "09:00", close: "18:00", closed: false }])
);

const EMPTY_LOCATION = {
  name: "",
  type: "clinic",
  phone: "",
  email: "",
  website: "",
  address: "",
  city: "",
  state: "",
  zip_code: "",
  hours: initialHours,
  description: "",
};

function MyLocations({ providers, active, onChange, onSwitch }) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = new
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const openAdd = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (p) => { setEditing(p); setEditorOpen(true); };

  const handleSaved = (saved, wasNew) => {
    if (wasNew) {
      onChange([saved, ...providers]);
      toast.success("Location added — pending review by IVTF admin");
    } else {
      onChange(providers.map((p) => (p.id === saved.id ? { ...p, ...saved } : p)));
      toast.success("Location updated");
    }
    setEditorOpen(false);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/providers/${toDelete.id}`);
      onChange(providers.filter((p) => p.id !== toDelete.id));
      toast.success(`Deleted "${toDelete.name}"`);
      setToDelete(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete location");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-semibold">My Locations</h2>
          <p className="text-sm text-stone-500 mt-0.5">
            {providers.length} {providers.length === 1 ? "location" : "locations"} · No limit
          </p>
        </div>
        <Button className="bg-[#064E3B] hover:bg-[#053d2f]" onClick={openAdd} data-testid="add-location-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add new location
        </Button>
      </div>

      {providers.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
          <MapPin className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500 mb-4">No locations yet. Add your first location to get started.</p>
          <Button className="bg-[#064E3B] hover:bg-[#053d2f]" onClick={openAdd}>
            <Plus className="w-4 h-4 mr-1.5" /> Add new location
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {providers.map((p) => (
            <div key={p.id} className={`bg-white rounded-xl border p-4 sm:p-5 transition ${active?.id === p.id ? "border-[#064E3B] ring-2 ring-[#064E3B]/10" : "border-stone-200 hover:border-stone-300"}`} data-testid={`location-row-${p.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-display font-semibold text-stone-900 truncate">{p.name}</h3>
                    <Badge className={
                      p.status === "active" ? "bg-[#10B981] text-white hover:bg-[#10B981] border-0" :
                      p.status === "pending" ? "bg-amber-500 text-white hover:bg-amber-500 border-0" :
                      "bg-stone-300 text-stone-700 hover:bg-stone-300 border-0"
                    }>{p.status}</Badge>
                    {active?.id === p.id && <Badge variant="outline" className="border-[#064E3B] text-[#064E3B] text-[10px]">Currently active</Badge>}
                  </div>
                  <div className="text-sm text-stone-600 space-y-0.5">
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-stone-400 shrink-0" />
                      <span className="truncate">{[p.address, p.city, p.state, p.zip_code].filter(Boolean).join(", ") || <span className="italic text-stone-400">No address</span>}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5 text-stone-400 shrink-0" />
                      <a href={`tel:${p.phone}`} className="hover:text-[#064E3B]">{p.phone || <span className="italic text-stone-400">No phone</span>}</a>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 lg:shrink-0">
                  {active?.id !== p.id && (
                    <Button size="sm" variant="ghost" onClick={() => onSwitch(p)} data-testid={`location-switch-${p.id}`}>
                      Make active
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`location-edit-${p.id}`}>
                    <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" asChild data-testid={`location-view-${p.id}`}>
                    <Link to={`/location/${p.slug}`} target="_blank">
                      <ExternalLink className="w-3.5 h-3.5 mr-1" /> View profile
                    </Link>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300"
                    onClick={() => setToDelete(p)}
                    data-testid={`location-delete-${p.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <LocationEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        editing={editing}
        onSaved={handleSaved}
      />

      <Dialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <DialogContent className="max-w-md" data-testid="location-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-rose-600">Delete location?</DialogTitle>
            <DialogDescription className="text-sm">
              Are you sure you want to delete <strong>{toDelete?.name}</strong>? This will permanently remove the location profile and all associated data (reviews, FAQs, appointment requests). This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setToDelete(null)} disabled={deleting}>Cancel</Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="location-delete-confirm"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Trash2 className="w-4 h-4 mr-1.5" /> Confirm delete</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LocationEditor({ open, onClose, editing, onSaved }) {
  const [form, setForm] = useState(EMPTY_LOCATION);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        name: editing.name || "",
        type: editing.type || "clinic",
        phone: editing.phone || "",
        email: editing.email || "",
        website: editing.website || "",
        address: editing.address || "",
        city: editing.city || "",
        state: editing.state || "",
        zip_code: editing.zip_code || "",
        hours: editing.hours && Object.keys(editing.hours).length ? editing.hours : initialHours,
        description: editing.description || "",
      });
    } else {
      setForm(EMPTY_LOCATION);
    }
  }, [open, editing]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Location name is required");
    if (!form.address.trim()) return toast.error("Street address is required");
    if (!form.city.trim() || !form.state.trim()) return toast.error("City and state are required");
    if (!form.zip_code.trim()) return toast.error("Zip code is required");
    if (!form.phone.trim()) return toast.error("Phone number is required");

    setSaving(true);
    try {
      let saved;
      let wasNew = false;
      if (editing?.id) {
        const { data } = await api.patch(`/providers/${editing.id}`, form);
        saved = data;
      } else {
        const { data } = await api.post("/providers", form);
        saved = data;
        wasNew = true;
      }
      onSaved(saved, wasNew);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save location");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="location-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{editing ? "Edit location" : "Add new location"}</DialogTitle>
          <DialogDescription className="sr-only">Location details form</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Location name <span className="text-rose-500">*</span></Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Beantown Boost Downtown"
              data-testid="loc-name"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Provider type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="loc-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="clinic">IV Clinic (fixed location)</SelectItem>
                  <SelectItem value="mobile">Mobile IV Service</SelectItem>
                  <SelectItem value="both">Both — Clinic & Mobile</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Phone <span className="text-rose-500">*</span></Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" data-testid="loc-phone" />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Street address <span className="text-rose-500">*</span></Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="123 Main St, Suite 100" data-testid="loc-address" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">City <span className="text-rose-500">*</span></Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="loc-city" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">State <span className="text-rose-500">*</span></Label>
              <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="MA" data-testid="loc-state" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Zip <span className="text-rose-500">*</span></Label>
              <Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} data-testid="loc-zip" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="hello@location.com" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Website</Label>
              <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://yourbusiness.com" />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Hours of operation</Label>
            <div className="bg-stone-50 rounded-lg border border-stone-200 p-3 space-y-1.5">
              {DAY_LABELS.map((d) => {
                const h = form.hours[d.key] || { open: "09:00", close: "18:00", closed: false };
                return (
                  <div key={d.key} className="grid grid-cols-[80px_70px_1fr_1fr] gap-2 items-center text-sm">
                    <span className="font-medium text-stone-700">{d.label}</span>
                    <label className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={!h.closed}
                        onChange={(e) => setForm({ ...form, hours: { ...form.hours, [d.key]: { ...h, closed: !e.target.checked } } })}
                        className="rounded"
                      />
                      <span className="text-xs text-stone-600">Open</span>
                    </label>
                    <Input type="time" disabled={h.closed} value={h.open} onChange={(e) => setForm({ ...form, hours: { ...form.hours, [d.key]: { ...h, open: e.target.value } } })} className="h-8 text-xs" />
                    <Input type="time" disabled={h.closed} value={h.close} onChange={(e) => setForm({ ...form, hours: { ...form.hours, [d.key]: { ...h, close: e.target.value } } })} className="h-8 text-xs" />
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">
              Description <span className="text-stone-400 normal-case font-normal">(formatted; up to 2,500 characters)</span>
            </Label>
            <RichTextEditor
              value={form.description}
              onChange={(html) => setForm((f) => ({ ...f, description: html }))}
              placeholder="What makes this location special? Hours, parking, walk-in policy, signature treatments…"
              variant="description"
              maxChars={2500}
            />
          </div>
        </div>

        <DialogFooter className="border-t border-stone-200 pt-4 mt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button className="bg-[#064E3B] hover:bg-[#053d2f]" onClick={save} disabled={saving} data-testid="loc-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Save className="w-4 h-4 mr-1.5" /> {editing ? "Save changes" : "Add location"}</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============== FAQs (per-location, max 10) ==============

const FAQ_MAX_PER_LOCATION = 10;
const FAQ_QUESTION_MAX = 200;
const FAQ_ANSWER_MAX = 1000;

function FAQManager({ provider, providers, onSwitch }) {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!provider) return;
    setLoading(true);
    api.get(`/providers/${provider.id}/faqs`)
      .then(({ data }) => setFaqs(data))
      .finally(() => setLoading(false));
  }, [provider?.id]);

  const count = faqs.length;
  const atMax = count >= FAQ_MAX_PER_LOCATION;

  const openAdd = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (f) => { setEditing(f); setEditorOpen(true); };

  const handleSaved = (saved, wasNew) => {
    if (wasNew) setFaqs((cur) => [...cur, saved]);
    else setFaqs((cur) => cur.map((it) => it.id === saved.id ? { ...it, ...saved } : it));
    setEditorOpen(false);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/faqs/${toDelete.id}`);
      setFaqs((cur) => cur.filter((f) => f.id !== toDelete.id));
      toast.success("FAQ deleted");
      setToDelete(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete FAQ");
    } finally {
      setDeleting(false);
    }
  };

  if (!provider) {
    return <p className="text-stone-500 text-sm">Select a location to manage its FAQs.</p>;
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-2 gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-semibold">Manage FAQs</h2>
          <p className="text-sm text-stone-500 mt-0.5">
            FAQs for <strong className="text-stone-700">{provider.name}</strong> · <span data-testid="faq-count">{count}/{FAQ_MAX_PER_LOCATION}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {providers.length > 1 && (
            <Select value={provider.id} onValueChange={(id) => onSwitch(providers.find((p) => p.id === id))}>
              <SelectTrigger className="w-[220px] h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button
            className="bg-[#064E3B] hover:bg-[#053d2f]"
            onClick={openAdd}
            disabled={atMax}
            data-testid="add-faq-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add new FAQ
          </Button>
        </div>
      </div>

      {atMax && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800 mb-4" data-testid="faq-limit-message">
          You have reached the maximum of {FAQ_MAX_PER_LOCATION} FAQs for this location. Delete an FAQ to add a new one.
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#064E3B]" /></div>
      ) : faqs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-stone-200 text-stone-500 text-sm">
          No FAQs yet. Click "Add new FAQ" to write your first one — they'll appear on your public location profile.
        </div>
      ) : (
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <div key={f.id} className="bg-white rounded-xl border border-stone-200 p-4" data-testid={`faq-row-${f.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold mb-1">Q{i + 1}</div>
                  <div className="font-display font-semibold text-stone-900 mb-1.5">{f.question}</div>
                  <p className="text-sm text-stone-600 whitespace-pre-wrap">{f.answer}</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openEdit(f)} data-testid={`faq-edit-${f.id}`}>
                    <Edit3 className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300"
                    onClick={() => setToDelete(f)}
                    data-testid={`faq-delete-${f.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <FAQEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        editing={editing}
        providerId={provider.id}
        onSaved={handleSaved}
      />

      <Dialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <DialogContent className="max-w-md" data-testid="faq-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-rose-600">Delete this FAQ?</DialogTitle>
            <DialogDescription className="text-sm">
              Are you sure you want to delete this FAQ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {toDelete && (
            <div className="bg-stone-50 rounded-lg p-3 text-sm">
              <div className="font-semibold text-stone-900 mb-1">{toDelete.question}</div>
              <p className="text-stone-600 line-clamp-3">{toDelete.answer}</p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setToDelete(null)} disabled={deleting}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={confirmDelete} disabled={deleting} data-testid="faq-delete-confirm">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Trash2 className="w-4 h-4 mr-1.5" /> Confirm delete</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FAQEditor({ open, onClose, editing, providerId, onSaved }) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQuestion(editing?.question || "");
    setAnswer(editing?.answer || "");
  }, [open, editing]);

  const onQChange = (e) => {
    const v = e.target.value;
    if (v.length <= FAQ_QUESTION_MAX) setQuestion(v);
  };
  const onAChange = (e) => {
    const v = e.target.value;
    if (v.length <= FAQ_ANSWER_MAX) setAnswer(v);
  };

  const save = async () => {
    if (!question.trim() || !answer.trim()) {
      toast.error("Both question and answer are required");
      return;
    }
    setSaving(true);
    try {
      let saved, wasNew = false;
      if (editing?.id) {
        const { data } = await api.patch(`/faqs/${editing.id}`, { question, answer });
        saved = data;
      } else {
        const { data } = await api.post(`/providers/${providerId}/faqs`, { question, answer });
        saved = data;
        wasNew = true;
      }
      toast.success(wasNew ? "FAQ added" : "FAQ updated");
      onSaved(saved, wasNew);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save FAQ");
    } finally {
      setSaving(false);
    }
  };

  const qLimit = question.length === FAQ_QUESTION_MAX;
  const aLimit = answer.length === FAQ_ANSWER_MAX;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="faq-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{editing ? "Edit FAQ" : "Add new FAQ"}</DialogTitle>
          <DialogDescription className="sr-only">FAQ question and answer</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Question <span className="text-rose-500">*</span></Label>
            <Input
              value={question}
              onChange={onQChange}
              placeholder="e.g. Do you offer same-day appointments?"
              maxLength={FAQ_QUESTION_MAX}
              data-testid="faq-question"
            />
            <div className={`text-[11px] mt-1 flex justify-between ${qLimit ? "text-rose-600 font-semibold" : "text-stone-400"}`} data-testid="faq-q-counter">
              <span>{qLimit ? "You have reached the maximum character limit for this field." : ""}</span>
              <span>{question.length}/{FAQ_QUESTION_MAX} characters</span>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Answer <span className="text-rose-500">*</span></Label>
            <Textarea
              value={answer}
              onChange={onAChange}
              placeholder="A clear, helpful answer your patients will appreciate."
              rows={5}
              maxLength={FAQ_ANSWER_MAX}
              data-testid="faq-answer"
            />
            <div className={`text-[11px] mt-1 flex justify-between ${aLimit ? "text-rose-600 font-semibold" : "text-stone-400"}`} data-testid="faq-a-counter">
              <span>{aLimit ? "You have reached the maximum character limit for this field." : ""}</span>
              <span>{answer.length}/{FAQ_ANSWER_MAX} characters</span>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 mt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button className="bg-[#064E3B] hover:bg-[#053d2f]" onClick={save} disabled={saving} data-testid="faq-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save FAQ"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============== ANALYTICS ==============
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { MousePointer, Navigation, Globe, Share2, TrendingUp, ArrowRight } from "lucide-react";

const KPI = [
  { key: "profile_view", label: "Profile views", icon: Eye, color: "#064E3B" },
  { key: "phone_click", label: "Phone clicks", icon: MousePointer, color: "#0EA5E9" },
  { key: "directions_click", label: "Directions taps", icon: Navigation, color: "#F59E0B" },
  { key: "website_click", label: "Website clicks", icon: Globe, color: "#8B5CF6" },
  { key: "share_click", label: "Shares", icon: Share2, color: "#EC4899" },
];

const RANGES = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
];

function Analytics({ provider, providers, onSwitch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("30d");
  const [scope, setScope] = useState(provider ? provider.id : "all"); // "all" or provider id

  useEffect(() => {
    setScope(provider?.id || "all");
  }, [provider?.id]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ range });
    if (scope !== "all") params.set("provider_id", scope);
    api.get(`/owner/analytics?${params.toString()}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [range, scope]);

  // Pretty-print x-axis (MM/DD)
  const fmtDate = (iso) => {
    const [_, m, d] = iso.split("-");
    return `${parseInt(m, 10)}/${parseInt(d, 10)}`;
  };

  return (
    <div data-testid="analytics-panel">
      <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-semibold">Analytics</h2>
          <p className="text-sm text-stone-500 mt-0.5">Profile performance for the selected scope and date range.</p>
        </div>
        <div className="flex items-center gap-2">
          {providers.length > 1 && (
            <Select value={scope} onValueChange={(v) => { setScope(v); if (v !== "all") onSwitch(providers.find((p) => p.id === v)); }}>
              <SelectTrigger className="w-[240px] h-9 text-xs" data-testid="analytics-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All locations ({providers.length})</SelectItem>
                {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-[150px] h-9 text-xs" data-testid="analytics-range"><SelectValue /></SelectTrigger>
            <SelectContent>
              {RANGES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading || !data ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#064E3B]" /></div>
      ) : (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
            {KPI.map((k) => {
              const Icon = k.icon;
              const val = data.totals?.[k.key] || 0;
              return (
                <div key={k.key} className="bg-white rounded-xl border border-stone-200 p-4" data-testid={`kpi-${k.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${k.color}15`, color: k.color }}>
                      <Icon className="w-4 h-4" />
                    </div>
                  </div>
                  <div className="font-display text-2xl font-semibold text-stone-900">{val.toLocaleString()}</div>
                  <div className="text-xs text-stone-500 mt-0.5">{k.label}</div>
                </div>
              );
            })}
          </div>

          {/* Leads + conversion tile */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            <div className="bg-[#064E3B] text-white rounded-xl p-4" data-testid="kpi-leads_total">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                  <Inbox className="w-4 h-4" />
                </div>
                <div className="text-xs uppercase tracking-wider opacity-80">Leads & inquiries</div>
              </div>
              <div className="font-display text-3xl font-semibold">{(data.leads_total || 0).toLocaleString()}</div>
              <div className="text-xs opacity-80 mt-1">Submitted via booking/inquiry forms</div>
            </div>
            <div className="bg-white rounded-xl border border-stone-200 p-4" data-testid="kpi-conversion_rate">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4" />
                </div>
                <div className="text-xs uppercase tracking-wider text-stone-500">View → Lead conversion</div>
              </div>
              <div className="font-display text-3xl font-semibold text-stone-900">{data.conversion_rate || 0}%</div>
              <div className="text-xs text-stone-500 mt-1">Of {data.totals?.profile_view || 0} profile views</div>
            </div>
          </div>

          {/* Views over time */}
          <div className="bg-white rounded-xl border border-stone-200 p-4 mb-5" data-testid="chart-views">
            <h3 className="font-display font-semibold mb-3 text-sm">Profile views over time</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.timeseries} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={fmtDate} fontSize={11} stroke="#a8a29e" tickMargin={5} />
                  <YAxis allowDecimals={false} fontSize={11} stroke="#a8a29e" />
                  <Tooltip
                    formatter={(value, name) => [value, name === "profile_view" ? "Profile views" : name]}
                    labelFormatter={(d) => new Date(d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                    contentStyle={{ fontSize: "12px", borderRadius: "8px", border: "1px solid #e7e5e4" }}
                  />
                  <Line type="monotone" dataKey="profile_view" stroke="#064E3B" strokeWidth={2.5} dot={{ fill: "#064E3B", r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* CTA breakdown bar chart */}
          <div className="bg-white rounded-xl border border-stone-200 p-4" data-testid="chart-cta">
            <h3 className="font-display font-semibold mb-3 text-sm">Daily CTA breakdown</h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.timeseries} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={fmtDate} fontSize={11} stroke="#a8a29e" tickMargin={5} />
                  <YAxis allowDecimals={false} fontSize={11} stroke="#a8a29e" />
                  <Tooltip
                    labelFormatter={(d) => new Date(d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                    contentStyle={{ fontSize: "12px", borderRadius: "8px", border: "1px solid #e7e5e4" }}
                  />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 11, paddingTop: 10 }} />
                  <Bar dataKey="phone_click" stackId="cta" fill="#0EA5E9" name="Phone" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="directions_click" stackId="cta" fill="#F59E0B" name="Directions" />
                  <Bar dataKey="website_click" stackId="cta" fill="#8B5CF6" name="Website" />
                  <Bar dataKey="share_click" stackId="cta" fill="#EC4899" name="Share" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {data.totals?.profile_view === 0 && (
            <div className="mt-5 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <strong>No traffic yet for this scope.</strong> Once visitors land on your public profile, you'll see views, clicks, and lead conversions appear here in real time.
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ============== Customer Q&A Manager (owner) ==============
const ANSWER_MAX = 500;

function QAManager({ provider, providers, onSwitch }) {
  const [tab, setTab] = useState("unanswered");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ unanswered: 0, answered: 0, hidden: 0 });
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(null);
  const [hideTarget, setHideTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!provider) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/owner/qa?provider_id=${provider.id}&status=${tab}`);
      setItems(data.items);
      setCounts(data.counts);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [provider?.id, tab]);

  const handleHide = async () => {
    if (!hideTarget) return;
    setBusy(true);
    try {
      await api.post(`/qa/${hideTarget.id}/hide`);
      toast.success("Question hidden");
      setHideTarget(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not hide"); }
    finally { setBusy(false); }
  };

  const handleUnhide = async (q) => {
    try {
      await api.post(`/qa/${q.id}/unhide`);
      toast.success("Question restored");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not unhide"); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await api.delete(`/qa/${deleteTarget.id}`);
      toast.success("Question deleted");
      setDeleteTarget(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not delete"); }
    finally { setBusy(false); }
  };

  if (!provider) return <p className="text-stone-500 text-sm">Select a location to manage its Q&A.</p>;

  return (
    <div data-testid="qa-manager">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-semibold">Manage Q&A</h2>
          <p className="text-sm text-stone-500 mt-0.5">Customer questions for <strong className="text-stone-700">{provider.name}</strong></p>
        </div>
        {providers.length > 1 && (
          <Select value={provider.id} onValueChange={(id) => onSwitch(providers.find((p) => p.id === id))}>
            <SelectTrigger className="w-[220px] h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="inline-flex bg-stone-100 rounded-lg p-0.5 mb-4">
        {[
          { v: "unanswered", label: "Unanswered", count: counts.unanswered },
          { v: "answered", label: "Answered", count: counts.answered },
          { v: "hidden", label: "Hidden", count: counts.hidden },
        ].map((t) => (
          <button
            key={t.v}
            onClick={() => setTab(t.v)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition flex items-center gap-2 ${tab === t.v ? "bg-white text-stone-900 shadow-sm" : "text-stone-500"}`}
            data-testid={`qa-subtab-${t.v}`}
          >
            {t.label}
            {t.count > 0 && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tab === t.v ? "bg-[#064E3B] text-white" : "bg-stone-200 text-stone-600"}`}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#064E3B]" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-stone-200 text-stone-500 text-sm">
          {tab === "unanswered" && "No unanswered questions. All caught up!"}
          {tab === "answered" && "No answered questions yet."}
          {tab === "hidden" && "No hidden questions."}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((q) => (
            <div key={q.id} className="bg-white rounded-xl border border-stone-200 p-4" data-testid={`qa-mgr-row-${q.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="text-xs text-stone-500">
                  <strong className="text-stone-900">{q.user_name}</strong>
                  {q.user_email && <span> · {q.user_email}</span>}
                  <span> · {new Date(q.created_at).toLocaleString()}</span>
                  {q.helpful_votes > 0 && tab !== "hidden" && <span className="ml-2 text-emerald-600 font-medium">👍 {q.helpful_votes}</span>}
                </div>
              </div>
              <p className="text-stone-900 font-medium mb-2 whitespace-pre-wrap">{q.question}</p>
              {q.answer && (
                <div className="bg-stone-50 rounded-lg border-l-2 border-[#064E3B] p-3 mb-3">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#064E3B] mb-1">Your response</div>
                  <p className="text-sm text-stone-700 whitespace-pre-wrap">{q.answer}</p>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {tab === "unanswered" && (
                  <>
                    <Button size="sm" className="bg-[#064E3B] hover:bg-[#053d2f]" onClick={() => setResponding(q)} data-testid={`qa-respond-${q.id}`}>
                      <Send className="w-3.5 h-3.5 mr-1.5" /> Respond
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setHideTarget(q)} data-testid={`qa-hide-${q.id}`}>
                      <EyeOff className="w-3.5 h-3.5 mr-1.5" /> Hide
                    </Button>
                  </>
                )}
                {tab === "answered" && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setResponding(q)} data-testid={`qa-edit-${q.id}`}>
                      <Edit3 className="w-3.5 h-3.5 mr-1.5" /> Edit Response
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setHideTarget(q)} data-testid={`qa-hide-${q.id}`}>
                      <EyeOff className="w-3.5 h-3.5 mr-1.5" /> Hide
                    </Button>
                    <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300" onClick={() => setDeleteTarget(q)} data-testid={`qa-delete-${q.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
                {tab === "hidden" && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => handleUnhide(q)} data-testid={`qa-unhide-${q.id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1.5" /> Unhide
                    </Button>
                    <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300" onClick={() => setDeleteTarget(q)} data-testid={`qa-delete-${q.id}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete permanently
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ResponseDialog
        open={!!responding}
        onClose={() => setResponding(null)}
        question={responding}
        onSaved={() => { setResponding(null); load(); }}
      />

      <Dialog open={!!hideTarget} onOpenChange={(o) => { if (!o) setHideTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="qa-hide-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Hide this question?</DialogTitle>
            <DialogDescription className="text-sm">
              Are you sure you want to hide this question? It will not be visible to customers, but you can unhide it later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setHideTarget(null)} disabled={busy}>Cancel</Button>
            <Button className="bg-[#064E3B] hover:bg-[#053d2f]" onClick={handleHide} disabled={busy} data-testid="qa-hide-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="qa-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-rose-600">Delete permanently?</DialogTitle>
            <DialogDescription className="text-sm">
              Are you sure you want to permanently delete this question and response? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={busy}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={handleDelete} disabled={busy} data-testid="qa-delete-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResponseDialog({ open, onClose, question, onSaved }) {
  const [answer, setAnswer] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setAnswer(question?.answer || "");
  }, [open, question]);

  const onChange = (e) => {
    const v = e.target.value;
    if (v.length <= ANSWER_MAX) setAnswer(v);
  };

  const submit = async () => {
    if (!answer.trim()) { toast.error("Response cannot be empty"); return; }
    setSaving(true);
    try {
      await api.post(`/qa/${question.id}/answer`, { answer });
      toast.success(question?.answer ? "Response updated" : "Your response has been posted");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post response");
    } finally { setSaving(false); }
  };

  if (!question) return null;
  const len = answer.length;
  const limitClass = len >= ANSWER_MAX ? "text-rose-600 font-semibold" : len > ANSWER_MAX * 0.8 ? "text-amber-600" : "text-stone-500";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="response-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{question.answer ? "Edit response" : `Respond to ${question.user_name}'s Question`}</DialogTitle>
          <DialogDescription className="sr-only">Post a public response</DialogDescription>
        </DialogHeader>
        <div className="bg-stone-50 rounded-lg p-3 my-3 text-sm">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-1">Customer question</div>
          <p className="text-stone-800 whitespace-pre-wrap">{question.question}</p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Your response <span className="text-rose-500">*</span></Label>
          <Textarea
            value={answer}
            onChange={onChange}
            rows={5}
            maxLength={ANSWER_MAX}
            placeholder="Type a helpful, friendly response…"
            autoFocus
            data-testid="response-text"
          />
          <div className={`text-[11px] mt-1 flex justify-between ${limitClass}`} data-testid="response-counter">
            <span>{len >= ANSWER_MAX ? `You have reached the maximum character limit (${ANSWER_MAX})` : ""}</span>
            <span>{len}/{ANSWER_MAX} characters</span>
          </div>
        </div>
        <DialogFooter className="gap-2 mt-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button className="bg-[#064E3B] hover:bg-[#053d2f]" onClick={submit} disabled={saving} data-testid="response-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (question.answer ? "Update Response" : "Post Response")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

